import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LuArrowUp, LuBookOpen } from "react-icons/lu";
import { ChatApi } from "../../../api/chatApi";
import {
  useAuthStore,
  useIsUserSignedIn,
} from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useSubscriptionActions, useSubscriptionStore } from "../../../core_stores/useSubscriptionStore";
import { useAuthModal } from "../../../providers/AuthModalProvid";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { trackRedditEvent } from "../../../utils/analyticsUtils";
import { formatNumberWithSpaces } from "../../../utils/numberFormatter";
import {
  ALLOWED_IMAGE_TYPES,
  useAttachedImage,
  useAttachedDesign,
  useIsPromptSubmitting,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachedDesignChip } from "../../common_components/AttachedDesignChip";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";
import { PulsingBorder } from "./PulsingBorder";
import { components } from "../../../design/tokens";
import { useDesignLibraryModal } from "../../../providers/DesignLibraryModalProvider";

export function WelcomeTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const attachedDesign = useAttachedDesign();
  const isPromptSubmitting = useIsPromptSubmitting();
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const placeholderContainerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const {
    setPrompt,
    setAttachedImage,
    setAttachedDesign,
    setIsSubmitting,
    isLengthExceeded: isPromptLengthExceeded,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { show: showAuthModal } = useAuthModal();
  const { open: openDesignLibrary } = useDesignLibraryModal();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();
  const { showSnackbar } = useSnackbar();

  // Focus on textarea when navigated with focusTextField flag
  useEffect(() => {
    if (location.state?.focusTextField && textareaRef.current) {
      textareaRef.current.focus();
      // Clear the state to prevent refocusing on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Focus textarea by default when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar(
        `Unsupported file type. Please select JPEG, PNG, GIF, or WebP images.`,
        { type: "error" }
      );
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveDesign = () => {
    setAttachedDesign(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  async function submit() {
    if (!prompt || prompt.trim() === "" || isPromptSubmitting) {
      return;
    }

    trackRedditEvent("generate_clicked");

    setIsSubmitting(true);
    try {
      if (!isUserSignedIn) {
        await showAuthModal();
        const isUserSignedInAfterAuthModal =
          useAuthStore.getState().user !== null;
        if (!isUserSignedInAfterAuthModal) {
          return;
        }
        // Load subscription data after login before checking tokens
        await reloadSubscriptionsInfo();
      }

      // Check if user has enough tokens before creating project
      const subscription = useSubscriptionStore.getState().subscription;
      if (!subscription || subscription.remaining <= 0) {
        openSubscriptionsModal();
        return;
      }

      // Combine user prompt with design prompt if a design is attached
      const finalPrompt = attachedDesign
        ? `${prompt}\n\n[[DESIGN_START]]\n${attachedDesign.promptText}\n[[DESIGN_END]]`
        : prompt;

      const projectId = await ChatApi.createNewProject();
      clearPromptInput();
      navigate(`/project/${projectId}`, {
        state: {
          projectId,
          initialPrompt: finalPrompt,
          initialImage: attachedImage,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`${isCompactView && "px-4"} w-full z-20`}>
      <PulsingBorder isFocused={isFocused}>
        <div
          className="h-full bg-dark-surface-secondary
          rounded-xl p-3 space-y-2
          border border-dark-border
          flex flex-col
          shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <div className="relative w-full flex-1">
            <textarea
              className="w-full h-[125px]
              bg-transparent rounded-lg
              border-0
              resize-none focus:outline-none
              text-dark-text
              py-3 px-4
              placeholder:text-dark-placeholder"
              value={prompt ?? ""}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              ref={textareaRef}
            />
          {!prompt && (
            <div ref={placeholderContainerRef} className="absolute top-3 left-4 right-4 z-0">
              <CustomPlaceholder containerRef={placeholderContainerRef} />
            </div>
          )}
        </div>
        {isPromptLengthExceeded() && (
          <div className="text-red-500 text-sm px-2">
            Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
            characters allowed.
          </div>
        )}
        <div className="flex justify-between items-center gap-4 px-2 pt-2 border-t border-dark-border">
          <div className={`flex items-center min-w-0 ${isCompactView ? 'gap-2' : 'gap-4'}`}>
            <AttachmentsBtn
              className="flex-shrink-0 w-9 h-9 bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full transition-colors duration-200"
              onAddImageClick={handleUploadClick}
              iconSize={20}
            />
            {!attachedDesign && (
              <button
                onClick={openDesignLibrary}
                className="bg-light-surface-tertiary dark:bg-dark-surface-tertiary
                  hover:bg-light-hover dark:hover:bg-dark-hover
                  text-dark-text-muted hover:text-dark-text rounded-full transition-colors duration-200
                  flex items-center justify-center gap-2
                  h-9 px-3"
                aria-label="Open Design Library"
              >
                <LuBookOpen size={20} />
                {!isCompactView && (
                  <span className="text-sm font-medium">Design</span>
                )}
              </button>
            )}
            {attachedImage && (
              <AttachedImageIcon
                attachedImg={attachedImage}
                onRemove={handleRemoveImage}
              />
            )}
            {attachedDesign && (
              <AttachedDesignChip
                designName={attachedDesign.name}
                description={attachedDesign.description}
                onRemove={handleRemoveDesign}
                iconOnly={isCompactView}
              />
            )}
          </div>

          <SubmitBtn onClick={submit} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />
        </div>
      </PulsingBorder>
    </div>
  );
}

function CustomPlaceholder({
  containerRef
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) {
  const [animatedText, setAnimatedText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [dotCount, setDotCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const { setPrompt } = usePromptInputActions();

  // Measure text width utility
  const measureTextWidth = (text: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;

    // Match the actual font used in the component
    context.font = '400 16px Roboto, system-ui, -apple-system, sans-serif';
    return context.measureText(text).width;
  };

  // Track container width with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth(); // Initial measurement

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  // Determine which text to use based on measurement
  const longText = "Ask Darvin to create a mobile app";
  const shortText = "Create a mobile app";

  const longTextWidth = measureTextWidth(longText + "..."); // Measure with 3 dots (worst case)
  const availableWidth = containerWidth - 64; // Account for padding (left-4 + right-4 from absolute positioning)
  const bufferWidth = availableWidth * 0.9; // 10% buffer

  const baseText = longTextWidth <= bufferWidth ? longText : shortText;
  const fullText = baseText + "...";

  // Typewriter effect
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setAnimatedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        setIsTypingComplete(true);
      }
    }, 50); // 50ms per character

    return () => clearInterval(interval);
  }, [fullText]);

  // Animated dots after typing completes
  useEffect(() => {
    if (!isTypingComplete) return;

    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 0 : prev + 1));
    }, 400); // 400ms per dot cycle

    return () => clearInterval(interval);
  }, [isTypingComplete]);

  const handlePlaceholderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPrompt("");
    // Focus the textarea with a small delay to ensure the text is set first
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        // Move cursor to end
        textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
      }
    }, 0);
  };

  const displayText = isTypingComplete
    ? baseText + ".".repeat(dotCount)
    : animatedText;

  return (
    <div
      className="text-dark-placeholder cursor-text"
      onClick={handlePlaceholderClick}
    >
      {displayText}
    </div>
  );
}

function SubmitBtn({ onClick }: { onClick: () => void }) {
  const { isLengthExceeded: isPromptLengthExceeded } = usePromptInputActions();
  const isPromptSubmitting = useIsPromptSubmitting();
  const isCompactView = useIsCompactView();
  const prompt = usePrompt();
  const isEmpty = !prompt || prompt.trim() === "";
  const isDisabled = isPromptSubmitting || isPromptLengthExceeded() || isEmpty;

  return (
    <div className="h-9 flex gap-2">
      <button
        id="home-generate"
        className={isCompactView
          ? `w-9 h-9 bg-primary-blue text-white rounded-full
             hover:opacity-90 transition-opacity duration-300
             disabled:opacity-50 disabled:cursor-not-allowed
             flex items-center justify-center`
          : `${components.button.primary} disabled:cursor-not-allowed whitespace-nowrap shrink-0`
        }
        onClick={onClick}
        disabled={isDisabled}
      >
        {isPromptSubmitting ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
        ) : isCompactView ? (
          <LuArrowUp className="w-5 h-5" />
        ) : (
          <span className="whitespace-nowrap">Build my app</span>
        )}
      </button>
    </div>
  );
}
